import React, { useMemo } from 'react'
import { Stack } from 'react-bootstrap'
import { IntegrationServiceId } from '../../../../common/integrations'
import { useGetIntegrationEntitiesQuery } from '../../../../store/integrations/integrations'
import { InfoCard } from '../Components/InfoCard'
import classNames from 'classnames'
import { type EventFilter } from '../../../../models/EventFilter'
import { type MultiSelectOption } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { useSalesforceEventEntityOptions } from '../../../ProfileSettings/EventFilters/useSalesforceEventEntityOptions'
import { conditionToActivePhrase, isFilterEffectivelyDisabled, type EntityLabelMap } from './calendarFilterUtils'
import stepStyles from './CalendarEventFiltersStep.module.scss'

const toLabelMap = (options: MultiSelectOption[]): Record<string, string> => Object.fromEntries(options.map((option) => [option.id, option.label]))

export interface CalendarEnterpriseFiltersViewProps {
  accountId: string
  serviceId: IntegrationServiceId
  autoJoinFilter?: EventFilter
  importOnlyFilter?: EventFilter
  isLoading: boolean
}

export const CalendarEnterpriseFiltersView: React.FC<CalendarEnterpriseFiltersViewProps> = ({ accountId, serviceId, autoJoinFilter, importOnlyFilter }) => {
  const isWealthbox = serviceId === IntegrationServiceId.WEALTHBOX
  const isRedtail = serviceId === IntegrationServiceId.REDTAIL
  const isSalesforce = serviceId === IntegrationServiceId.SALESFORCE

  const { data: wealthboxEventCategories } = useGetIntegrationEntitiesQuery(
    { accountId, connector: IntegrationServiceId.WEALTHBOX, entityType: 'event_categories' },
    { skip: !isWealthbox },
  )
  const { data: redtailActivityCodes } = useGetIntegrationEntitiesQuery(
    { accountId, connector: IntegrationServiceId.REDTAIL, entityType: 'activity_codes' },
    { skip: !isRedtail },
  )
  const { data: redtailCategories } = useGetIntegrationEntitiesQuery(
    { accountId, connector: IntegrationServiceId.REDTAIL, entityType: 'categories' },
    { skip: !isRedtail },
  )
  const { options: salesforceEventTypes } = useSalesforceEventEntityOptions(accountId, 'event_types', isSalesforce)
  const { options: salesforceEventSubtypes } = useSalesforceEventEntityOptions(accountId, 'event_subtypes', isSalesforce)

  const entityLabels = useMemo(
    (): EntityLabelMap => ({
      wealthbox_event_type: Object.fromEntries(
        (wealthboxEventCategories?.data?.eventCategories ?? []).flatMap((e) => (e.name ? [[e.id.toString(), e.name] as const] : [])),
      ),
      redtail_type: Object.fromEntries((redtailActivityCodes?.data?.activityCodes ?? []).map((e) => [e.id.toString(), e.name])),
      redtail_category: Object.fromEntries((redtailCategories?.data?.categories ?? []).map((e) => [e.id.toString(), e.name])),
      salesforce_event_type: toLabelMap(salesforceEventTypes),
      salesforce_event_subtype: toLabelMap(salesforceEventSubtypes),
    }),
    [wealthboxEventCategories, redtailActivityCodes, redtailCategories, salesforceEventTypes, salesforceEventSubtypes],
  )

  const importOnlyConditions = importOnlyFilter?.conditions.filter((c) => c.enforced) ?? []
  const autoJoinConditions = autoJoinFilter?.conditions.filter((c) => c.enforced) ?? []

  const isAutoJoinDisabled = isFilterEffectivelyDisabled(autoJoinConditions)
  const isImportOnlyDisabled = isFilterEffectivelyDisabled(importOnlyConditions)

  return (
    <div className={classNames(stepStyles.conditionsBlock, 'd-flex flex-column gap-2')}>
      {importOnlyConditions.length ? (
        <>
          <div className='h5 mb-1 align-self-center'>Your enterprise has set these meeting sync rules</div>
          <Stack gap={2}>
            {isImportOnlyDisabled ? (
              <InfoCard label='Meeting sync disabled' />
            ) : (
              importOnlyConditions.map((condition) => <InfoCard key={condition.id} label={conditionToActivePhrase(condition, 'import_only', entityLabels)} />)
            )}
          </Stack>
        </>
      ) : null}
      {autoJoinConditions.length ? (
        <>
          <div className='h5 mb-1 mt-2 align-self-center'>Your enterprise has set these auto-join rules</div>
          <Stack gap={2}>
            {isAutoJoinDisabled ? (
              <InfoCard label='Auto join disabled' />
            ) : (
              autoJoinConditions.map((condition) => <InfoCard key={condition.id} label={conditionToActivePhrase(condition, 'auto_join', entityLabels)} />)
            )}
          </Stack>
        </>
      ) : null}
    </div>
  )
}
